'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import MaskedInput from './MaskedInput';
import { validateCPF } from '@/lib/validators';
import { interestOptions } from '@/lib/constants';
import FieldWrapper from './FieldWrapper';
import { supabase } from '@/lib/supabase';

interface PFFormData {
  cpf: string;
  nome: string;
  email: string;
  celular: string;
  interesse: string;
  informacoes_complementares: string;
}

interface Props {
  onSuccess: () => void;
}

export default function PessoaFisicaForm({ onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<PFFormData>({ mode: 'onBlur' });

  const cpfValue = watch('cpf') ?? '';
  const infoCompl = watch('informacoes_complementares') ?? '';

  const handleCPFBlur = () => {
    const raw = cpfValue.replace(/\D/g, '');
    if (raw.length === 11 && !validateCPF(raw)) {
      setError('cpf', { message: 'CPF inválido — verifique os dígitos.' });
    } else if (raw.length === 11) {
      clearErrors('cpf');
    }
  };

  const onSubmit = async (data: PFFormData) => {
    const rawCPF = data.cpf.replace(/\D/g, '');
    if (!validateCPF(rawCPF)) {
      setError('cpf', { message: 'CPF inválido — verifique os dígitos.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const { error } = await supabase.from('adhesion_registrations').insert({
        type: 'pf',
        cpf: rawCPF,
        nome: data.nome,
        email: data.email,
        celular: data.celular.replace(/\D/g, ''),
        interesse: data.interesse,
        informacoes_complementares: data.informacoes_complementares || null,
      });

      if (error) throw error;
      onSuccess();
    } catch {
      setSubmitError(
        'Ocorreu um erro ao enviar o cadastro. Por favor, tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex flex-col gap-5">
        {/* CPF */}
        <FieldWrapper
          label="CPF"
          required
          error={errors.cpf?.message}
          helper="Digite apenas os números — a formatação é automática."
        >
          <MaskedInput
            mask="999.999.999-99"
            placeholder="000.000.000-00"
            value={cpfValue}
            onChange={(val) => {
              setValue('cpf', val, { shouldValidate: false });
              clearErrors('cpf');
            }}
            onBlur={handleCPFBlur}
            hasError={!!errors.cpf}
            aria-label="CPF"
            id="pf-cpf"
          />
        </FieldWrapper>

        {/* Nome */}
        <FieldWrapper label="Nome Completo" required error={errors.nome?.message}>
          <input
            id="pf-nome"
            type="text"
            placeholder="Ex.: João Carlos da Silva"
            className={`input-base ${errors.nome ? 'input-error' : ''}`}
            autoComplete="name"
            {...register('nome', {
              required: 'Nome completo é obrigatório.',
              minLength: { value: 3, message: 'Nome deve ter ao menos 3 caracteres.' },
            })}
          />
        </FieldWrapper>

        {/* E-mail */}
        <FieldWrapper label="E-mail" required error={errors.email?.message}>
          <input
            id="pf-email"
            type="email"
            placeholder="seuemail@exemplo.com.br"
            className={`input-base ${errors.email ? 'input-error' : ''}`}
            autoComplete="email"
            {...register('email', {
              required: 'E-mail é obrigatório.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Informe um e-mail válido.',
              },
            })}
          />
        </FieldWrapper>

        {/* Celular */}
        <FieldWrapper
          label="Celular / WhatsApp"
          required
          error={errors.celular?.message}
          helper="Preferimos WhatsApp para agilizar o contato."
        >
          <MaskedInput
            mask="(99) 99999-9999"
            placeholder="(51) 99999-9999"
            value={watch('celular') ?? ''}
            onChange={(val) => {
              setValue('celular', val, { shouldValidate: true });
            }}
            onBlur={() => {
              const raw = (watch('celular') ?? '').replace(/\D/g, '');
              if (raw.length > 0 && raw.length < 11) {
                setError('celular', { message: 'Celular incompleto — informe o DDD e os 9 dígitos.' });
              } else {
                clearErrors('celular');
              }
            }}
            hasError={!!errors.celular}
            aria-label="Celular / WhatsApp"
            id="pf-celular"
          />
          <input
            type="hidden"
            {...register('celular', {
              required: 'Celular / WhatsApp é obrigatório.',
              validate: (v) => {
                const raw = (v ?? '').replace(/\D/g, '');
                return raw.length === 11 || 'Celular incompleto — informe o DDD e os 9 dígitos.';
              },
            })}
          />
        </FieldWrapper>

        {/* Interesse */}
        <FieldWrapper
          label="Interesse Principal"
          required
          error={errors.interesse?.message}
        >
          <select
            id="pf-interesse"
            className={`input-base ${errors.interesse ? 'input-error' : ''}`}
            {...register('interesse', {
              required: 'Selecione seu interesse principal.',
              validate: (v) => v !== '' || 'Selecione seu interesse principal.',
            })}
            defaultValue=""
          >
            <option value="" disabled>
              Selecione uma opção...
            </option>
            {interestOptions.map((opt) => (
              <option key={`pf-interest-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FieldWrapper>

        {/* Informações Complementares */}
        <FieldWrapper
          label="Informações Complementares"
          error={errors.informacoes_complementares?.message}
          helper={`${infoCompl.length}/300 caracteres`}
        >
          <textarea
            id="pf-informacoes-complementares"
            placeholder="Descreva informações adicionais relevantes sobre seu rebanho, propriedade ou necessidades específicas..."
            rows={4}
            className={`input-base resize-none ${errors.informacoes_complementares ? 'input-error' : ''}`}
            {...register('informacoes_complementares', {
              maxLength: { value: 300, message: 'Máximo de 300 caracteres permitidos.' },
            })}
          />
        </FieldWrapper>

        {/* Submit error */}
        {submitError && (
          <div
            className="rounded-lg px-4 py-3 text-sm flex items-start gap-2"
            style={{
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error)',
              border: '1px solid rgba(192,57,43,0.2)',
            }}
            role="alert"
          >
            <span className="mt-0.5 flex-shrink-0">⚠️</span>
            <span>{submitError}</span>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full mt-2"
          style={{ height: '52px' }}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg
                className="spin"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>Enviando cadastro...</span>
            </>
          ) : (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22 11 13 2 9l20-7z" />
              </svg>
              <span>Enviar Cadastro</span>
            </>
          )}
        </button>

        <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
          Ao enviar, você concorda com nossa{' '}
          <a
            href="https://qrcattle.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: 'var(--primary)' }}
          >
            Política de Privacidade
          </a>{' '}
          (LGPD).
        </p>
      </div>
    </form>
  );
}